import { bunja } from 'bunja';
import { atom } from 'jotai';

import Num from '@flipster/utils/num';
import { positionOrderSort } from '@flipster/utils/sort';
import type { OpenPosition } from '@flipster/utils/types/position';

import { positionsAtom } from './internal/fixed-topics/position';
import { marketsBunja } from './market';
import { ConditionalOrderType, ConditionalOrderTypeEnum, ordersBunja } from './order';
import { r230522SocketBunja } from './socket';

export type * from './internal/fixed-topics/position';

const v2PositionsAtom = atom((get) => {
  const positions = get(positionsAtom);
  if (!positions) return null;

  return (
    Object.entries(positions)
      // Assure creation order (slot id is order by created time)
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([, value]) => value)
      .map((position) => {
        /**
         * unrealized pnl percentage on isolated margin = unrealizedPnl / (isolatedMarginInitial + isolatedMarginAdded) * 100
         * unrealized pnl percentage on cross margin = unrealizedPnl / initPositionMargin * 100
         */
        const calcUnrealizedPnlPercent = (() => {
          switch (position.marginType) {
            case 'Cross':
              if (position.initPositionMargin === '0') return '0';
              return Num(position.unrealizedPnl).dividedBy(position.initPositionMargin).multipliedBy(100).toString();
            case 'Isolated': {
              const margin = Num(position.isolatedMarginInitial).plus(position.isolatedMarginAdded || '0');
              if (margin.isZero()) return '0';
              return Num(position.unrealizedPnl).dividedBy(margin).multipliedBy(100).toString();
            }
            default:
              // oxlint-disable-next-line no-unused-expressions
              position.marginType satisfies never;
              // oxlint-disable-next-line no-console
              console.error(`Unexpected margin type: ${position.marginType}`);
              return '0';
          }
        })();

        /**
         * net pnl percentage on isolated margin = netPnl / (isolatedMarginInitial + isolatedMarginAdded) * 100
         * net pnl percentage on cross margin = netPnl / initPositionMargin * 100
         */
        const calcNetPnlPercent = (() => {
          switch (position.marginType) {
            case 'Cross':
              if (position.initPositionMargin === '0') return '0';
              return Num(position.netPnl).dividedBy(position.initPositionMargin).multipliedBy(100).toString();
            case 'Isolated': {
              const margin = Num(position.isolatedMarginInitial).plus(position.isolatedMarginAdded || '0');
              if (margin.isZero()) return '0';
              return Num(position.netPnl).dividedBy(margin).multipliedBy(100).toString();
            }
            default:
              // oxlint-disable-next-line no-unused-expressions
              position.marginType satisfies never;
              // oxlint-disable-next-line no-console
              console.error(`Unexpected margin type: ${position.marginType}`);
              return '0';
          }
        })();

        return {
          ...position,
          calcUnrealizedPnlPercent,
          calcNetPnlPercent,
        };
      })
  );
});

export const positionsBunja = bunja(() => {
  bunja.use(r230522SocketBunja);
  return {
    positionsAtom,
    v2PositionsAtom,
  };
});
positionsBunja.debugLabel = 'positionsBunja';

export const openPositionsBunja = bunja(() => {
  const positionsBunjaInstance = bunja.use(positionsBunja);
  const { v2OrdersAtom } = bunja.use(ordersBunja);
  const { marketsAtom } = bunja.use(marketsBunja);
  const openPositionsAtom = atom((get) => {
    const positions = get(positionsBunjaInstance.v2PositionsAtom);
    const orders = get(v2OrdersAtom);
    const markets = get(marketsAtom);
    if (!positions) return {};

    return positions
      .filter((p) => p.position !== '0')
      .sort(positionOrderSort)
      .reduce((acc: Record<string, OpenPosition[]>, p) => {
        acc[p.symbol] ??= [];
        const fundsInvested = (() => {
          switch (p.marginType) {
            case 'Cross':
              return p.initPositionMargin;
            case 'Isolated':
              return Num(p.isolatedMarginInitial)
                .plus(p.isolatedMarginAdded || '0')
                .toString();
            default:
              // oxlint-disable-next-line no-unused-expressions
              p.marginType satisfies never;
              // oxlint-disable-next-line no-console
              console.error(`Unexpected margin type: ${p.marginType}`);
              return '0';
          }
        })();

        const side = Num(p.position).isGreaterThanOrEqualTo(0) ? 'Long' : 'Short';
        const size = Num(p.position).multipliedBy(p.midPrice).toString();
        const markPrice = markets?.[p.symbol]?.markPrice || '0';

        // 서버에서 음수로 내려 올수도 있음 유저에게는 음수로 보여주지 않도록 함
        let liquidationPrice: string | undefined = p?.liquidationPrice;
        if (liquidationPrice && Num(liquidationPrice).isLessThan(0)) liquidationPrice = undefined;

        const openPositionType: ConditionalOrderType[] = [
          ConditionalOrderTypeEnum.PositionTp,
          ConditionalOrderTypeEnum.PositionSl,
        ];
        const filteredTpSl = orders?.[p.symbol]?.filter(
          (o) => openPositionType.includes(o.conditionalOrderType) && o.slot === p.slot,
        );

        // (OrderSide::Long, TriggerDirection::Up) | (OrderSide::Short, TriggerDirection::Down) => SL
        const slOrder = filteredTpSl?.find(
          (v) =>
            (v.side === 'Long' && v.triggerDirection === 'Up') || (v.side === 'Short' && v.triggerDirection === 'Down'),
        );
        // (OrderSide::Long, TriggerDirection::Down) | (OrderSide::Short, TriggerDirection::Up) => TP
        const tpOrder = filteredTpSl?.find(
          (v) =>
            (v.side === 'Long' && v.triggerDirection === 'Down') || (v.side === 'Short' && v.triggerDirection === 'Up'),
        );
        const stopLoss = slOrder?.triggerPrice || '0';
        const takeProfit = tpOrder?.triggerPrice || '0';
        const slOrderQty = slOrder?.qty;
        const tpOrderQty = tpOrder?.qty;

        acc[p.symbol].push({
          ...p,
          stopLoss,
          slOrderQty,
          takeProfit,
          tpOrderQty,
          side,
          liquidationPrice,
          fundsInvested,
          quantity: p.position,
          size,
          markPrice,
        });
        return acc;
      }, {});
  });
  openPositionsAtom.debugLabel = 'openPositionsAtom';

  const topOpenPositionsAtom = atom((get) => {
    const openPositions = get(openPositionsAtom);
    if (!openPositions || Object.keys(openPositions).length < 1) return undefined;
    return Object.values(openPositions)
      .map((v) =>
        v.reduce((acc: Record<string, string>, cur) => {
          acc.symbol = cur.symbol;
          acc.amount = Num(cur.position)
            .multipliedBy(cur.midPrice)
            .abs()
            .plus(acc.amount || '0')
            .toString();
          return acc;
        }, {}),
      )
      .sort((a, b) => (Num(b.amount).isGreaterThan(Num(a.amount)) ? 1 : -1))
      .map((v) => v.symbol)
      .slice(0, 3);
  });
  topOpenPositionsAtom.debugLabel = 'topOpenPositionsAtom';

  const openSymbolsAtom = atom((get) => {
    const openPositions = get(openPositionsAtom);
    if (!openPositions || Object.keys(openPositions).length < 1) return [];
    return Object.keys(openPositions)
      .filter((position) => openPositions[position].length > 0)
      .sort((a, b) => {
        const bOpenPosition = openPositions[b].at(-1);
        const aOpenPosition = openPositions[a].at(-1);
        if (!bOpenPosition || !aOpenPosition) return 0;
        return Num(bOpenPosition.openTime).minus(aOpenPosition.openTime).toNumber();
      });
  });

  return { openPositionsAtom, topOpenPositionsAtom, openSymbolsAtom };
});
openPositionsBunja.debugLabel = 'openPositionsBunja';
